import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, User, ArrowRight, Check, MessageCircleQuestion, Menu, Loader2, AlertCircle, Sparkles, TrendingUp, GraduationCap, ChevronLeft, ChevronRight, Volume2, VolumeX, Play } from "lucide-react";

// Deployed API Gateway URL that fronts the Lambda
const SUBMIT_ENDPOINT = "https://rglvxgwa53.execute-api.ap-south-1.amazonaws.com/dev/uploadformlanding";

const CATEGORY_CARDS = [
  {
    title: "Daily Life English",
    bullets: ["Phone calls", "Small talk", "Daily conversations"],
    cta: "Speak naturally and confidently",
  },
  {
    title: "Professional English",
    bullets: ["Client communication", "Meetings", "Team discussions", "Email etiquette"],
    cta: "Communicate clearly and make an impact",
  },
  {
    title: "Interview English",
    bullets: ["Confidence in HR rounds", "Clear answer structure", "Express ideas effectively"],
    cta: "Stand out and get hired",
  },
];

const FEATURES = [
  {
    icon: User,
    title: "Personal Trainer",
    subtitle: "Learn with one dedicated trainer",
    bullets: ["Get personal attention", "Get feedback on your speaking", "Improve step by step"],
  },
  {
    icon: MessageCircleQuestion,
    title: "Customised Training",
    subtitle: "A plan made for you",
    bullets: ["Based on your level", "Based on your goals", "Learn at your own pace"],
  },
  {
    icon: Check,
    title: "Based on Your Needs",
    subtitle: "Learn what you need",
    bullets: ["Daily conversations", "Office communication", "Interview practice"],
  },
  {
    icon: Sparkles,
    title: "AI-Powered Support",
    subtitle: "Practise anytime",
    bullets: ["Speak and practise daily", "Get instant help", "Build confidence"],
  },
  {
    icon: TrendingUp,
    title: "Continuous Follow-Up",
    subtitle: "We track your progress",
    bullets: ["Regular follow-ups", "Simple feedback", "Help you grow step by step"],
  },
  {
    icon: GraduationCap,
    title: "Expert Trainers",
    subtitle: "Learn from experts",
    bullets: ["British Council–certified trainers", "Easy teaching style", "Focus on speaking"],
  },
];


const VIDEO_TESTIMONIALS = [
  "1LNIefp2YYg",
  "v1uEpqUHjp0",
  "3lXhofV_Mrg",
  "h4bbe-3zSiE",
];

export default function AnglofoneLanding() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", reason: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [ytApiReady, setYtApiReady] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);

  const sectionRef = useRef(null);
  const videoContainerRef = useRef(null);
  const playerRef = useRef(null);
  const isMountedRef = useRef(true);
  const sectionInViewRef = useRef(false);
  const isMutedRef = useRef(true); // mirrors isMuted, readable inside event callbacks without stale closures

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);


  const tryUnmute = (player) => {
    if (!player) return;
    try {
      player.unMute();
      player.playVideo();
      setIsMuted(false);
    } catch (e) {
      // Blocked by the browser — keep playing muted, no visible disruption
      try { player.playVideo(); } catch (_) {}
    }
  };


  useEffect(() => {
    const handleUserInteraction = () => {
      if (userInteracted) return;
      setUserInteracted(true);
      if (isMutedRef.current && sectionInViewRef.current) {
        tryUnmute(playerRef.current);
      }
    };


    document.addEventListener("click", handleUserInteraction);
    document.addEventListener("keydown", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);

    return () => {
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };
  }, [userInteracted]);

  // ── Load the YouTube IFrame API once ────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;

    if (window.YT && window.YT.Player) {
      setYtApiReady(true);
      return;
    }

    if (!document.getElementById("youtube-iframe-api")) {
      const tag = document.createElement("script");
      tag.id = "youtube-iframe-api";
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }

    window.onYouTubeIframeAPIReady = () => {
      if (isMountedRef.current) setYtApiReady(true);
    };

    return () => {
      isMountedRef.current = false;
      if (playerRef.current && playerRef.current.destroy) {
        try { playerRef.current.destroy(); } catch (e) {}
        playerRef.current = null;
      }
    };
  }, []);

  // ── Create the player exactly once, on a DOM node React never
  //    unmounts. Always starts muted+autoplaying — the one form of
  //    autoplay every browser allows unconditionally. ─────────────────
  useEffect(() => {
    if (!ytApiReady || !videoContainerRef.current || playerRef.current) return;

    playerRef.current = new window.YT.Player(videoContainerRef.current, {
      videoId: VIDEO_TESTIMONIALS[0],
      playerVars: {
        autoplay: 1,
        mute: 1,
        playsinline: 1,
        modestbranding: 1,
        rel: 0,
        origin: window.location.origin,
      },
      events: {
        onReady: (event) => {
          if (!isMountedRef.current) return;
          setPlayerReady(true);
          event.target.mute();
          setIsMuted(true);
          event.target.playVideo();
          if (userInteracted && sectionInViewRef.current) {
            tryUnmute(event.target);
          }
        },
        onStateChange: (event) => {
          if (!isMountedRef.current) return;
          // Safety net: never let the video sit paused while its section
          // is visible — this is what catches any browser-blocked
          // unmute attempt and just keeps it going (muted if it must).
          if (event.data === window.YT.PlayerState.PAUSED && sectionInViewRef.current) {
            event.target.playVideo();
          }
          if (event.data === window.YT.PlayerState.ENDED) {
            event.target.seekTo(0);
            event.target.playVideo();
          }
        },
        onError: () => {
          if (playerRef.current && isMountedRef.current) {
            try {
              playerRef.current.loadVideoById(VIDEO_TESTIMONIALS[currentVideo]);
            } catch (e) {}
          }
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ytApiReady]);

  

  useEffect(() => {
    if (!playerReady || !playerRef.current || !isMountedRef.current) return;
    const player = playerRef.current;
    const videoId = VIDEO_TESTIMONIALS[currentVideo];
    if (typeof player.loadVideoById !== "function") return;

    player.loadVideoById(videoId);
    if (isMutedRef.current) {
      player.mute();
    } else {
      // Audio was already unlocked this session — keep it unlocked.
      player.unMute();
    }
    player.playVideo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVideo, playerReady]);

  // ── Track visibility: play when scrolled to, pause when scrolled away.
  //    Only ATTEMPTS to unmute if real interaction already happened. ───
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || !playerReady) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          sectionInViewRef.current = entry.isIntersecting;
          const player = playerRef.current;
          if (!player) return;

          if (entry.isIntersecting) {
            player.playVideo();
            if (userInteracted && isMutedRef.current) {
              tryUnmute(player);
            }
          } else {
            player.pauseVideo();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [playerReady, userInteracted]);

  const toggleMute = () => {
    const player = playerRef.current;
    if (!player) return;
    setUserInteracted(true); // a direct click is always a real gesture
    if (isMuted) {
      player.unMute();
      player.playVideo();
      setIsMuted(false);
    } else {
      player.mute();
      setIsMuted(true);
    }
  };

  const goToPrevVideo = () => {
    setCurrentVideo((prev) => (prev - 1 + VIDEO_TESTIMONIALS.length) % VIDEO_TESTIMONIALS.length);
  };

  const goToNextVideo = () => {
    setCurrentVideo((prev) => (prev + 1) % VIDEO_TESTIMONIALS.length);
  };

  const goToSpecificVideo = (index) => {
    setCurrentVideo(index);
  };

  const closeModal = () => {
    setOpen(false);
    setTimeout(() => {
      setStatus("idle");
      setErrorMsg("");
      setForm({ name: "", phone: "", email: "", reason: "" });
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;

    setStatus("loading");
    setErrorMsg("");

    try {
      const res = await fetch(SUBMIT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email || "",
          reason: form.reason,
        }),
      });

      const resData = await res.json();

      if (!res.ok) {
        if (res.status === 400 || res.status === 409) {
          throw new Error(resData.error || "Invalid submission.");
        } else {
          throw new Error("Request failed");
        }
      }

      sessionStorage.setItem("af_signup_details", JSON.stringify(form));

      const params = new URLSearchParams({
        name: form.name,
        phone: form.phone,
        email: form.email || "",
      });

      navigate(`/thank-you?${params.toString()}`);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Couldn't submit right now. Please check your connection and try again.");
    }
  };

  return (
    <div className="af-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap');

        /* Defined on :root (not .af-root) so these variables are
           available to EVERYTHING in the document, including modals
           rendered via createPortal onto document.body, which sit
           outside .af-root in the real DOM and would otherwise fail to
           resolve any var(--x) reference. */
        :root {
          --red: #C81E3A;
          --red-deep: #8E1229;
          --ink: #250709;
          --bg-dark: #29070B;
          --bg-darker: #1B0508;
          --paper: #FBEFE7;
          --paper-dim: #F0DCCF;
          --gold: #D9A441;
          --line: rgba(251,239,231,0.14);
        }

        .af-root {
          font-family: 'Inter', sans-serif;
          background: var(--paper);
          color: var(--ink);
          width: 100%;
          overflow-x: hidden;
        }
        .af-root * { box-sizing: border-box; margin: 0; padding: 0; }
        .af-root h1, .af-root h2, .af-root h3 {
          font-family: 'Fraunces', serif;
          font-weight: 600;
          letter-spacing: -0.01em;
        }
        .af-eyebrow {
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .af-announce {
          background: var(--ink);
          color: var(--paper);
          text-align: center;
          font-size: 13px;
          padding: 9px 16px;
          font-family: 'JetBrains Mono', monospace;
          letter-spacing: 0.02em;
        }
        .af-announce b { color: var(--gold); font-weight: 500; }

        .af-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 48px;
          background: var(--bg-dark);
        }
        .af-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          color: var(--paper);
          font-family: 'Fraunces', serif;
          font-size: 22px;
          font-weight: 700;
        }
        .af-logo-img {
          height: 60px;
          width: auto;
          object-fit: contain;
          display: block;
        }
        .af-navright { display: flex; align-items: center; gap: 18px; }
        .af-menu-btn {
          display: none;
          background: none;
          border: none;
          color: var(--paper);
          cursor: pointer;
          padding: 6px;
        }
        .af-mobile-menu {
          display: none;
        }

        .af-btn {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 16px;
          border: none;
          border-radius: 100px;
          padding: 16px 34px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
        }
        .af-btn:active { transform: scale(0.97); }
        .af-btn-primary {
          background: linear-gradient(135deg, var(--red), var(--red-deep));
          color: var(--paper);
          box-shadow: 0 8px 20px -6px rgba(200,30,58,0.55);
        }
        .af-btn-primary:hover { box-shadow: 0 10px 26px -6px rgba(200,30,58,0.75); }

        /* Highly visible attention highlight for the Join now CTAs —
           expanding glow ring + a scale/brightness pulse. */
        .af-btn-attn {
          position: relative;
          animation: af-pulse-scale 1.3s ease-in-out infinite;
        }
        .af-btn-attn::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 100px;
          box-shadow: 0 0 0 0 rgba(200,30,58,0.65);
          animation: af-pulse-ring 1.3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          pointer-events: none;
        }
        @keyframes af-pulse-scale {
          0%, 100% {
            transform: scale(1);
            filter: brightness(1);
          }
          50% {
            transform: scale(1.06);
            filter: brightness(1.18);
          }
        }
        @keyframes af-pulse-ring {
          0% {
            box-shadow: 0 0 0 0 rgba(200,30,58,0.65);
          }
          70% {
            box-shadow: 0 0 0 14px rgba(200,30,58,0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(200,30,58,0);
          }
        }

        .af-hero {
          background:
            radial-gradient(ellipse 900px 500px at 15% 0%, rgba(200,30,58,0.35), transparent 60%),
            linear-gradient(180deg, var(--bg-dark), var(--bg-darker));
          padding: 90px 48px 110px;
          display: grid;
          grid-template-columns: 1.05fr 0.95fr;
          gap: 40px;
          align-items: center;
        }
        .af-hero h1 {
          color: var(--paper);
          font-size: clamp(32px, 5.2vw, 56px);
          line-height: 1.08;
          max-width: 640px;
        }
        .af-hero h1 em {
          font-style: normal;
          color: var(--red);
          background: linear-gradient(180deg, transparent 62%, rgba(200,30,58,0.4) 62%);
        }
        .af-hero p.sub {
          color: var(--paper-dim);
          font-size: 17px;
          line-height: 1.6;
          margin: 24px 0 34px;
          max-width: 500px;
        }
        .af-hero-ctas { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
        .af-hero-ctas .af-btn {
          font-size: 18px;
          padding: 18px 40px;
        }

        .af-cards {
          position: relative;
          height: 440px;
        }
        .af-card {
          position: absolute;
          width: 300px;
          background: var(--paper);
          border-radius: 18px;
          padding: 22px 24px;
          box-shadow: 0 30px 60px -20px rgba(0,0,0,0.55);
        }
        .af-card .en {
          font-family: 'Fraunces', serif;
          font-size: 20px;
          font-weight: 600;
          color: var(--ink);
        }
        .af-card .bullets {
          font-family: 'Inter', sans-serif;
          font-size: 12.5px;
          line-height: 1.5;
          color: #6b4a45;
          margin-top: 8px;
        }
        .af-card .cta {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11.5px;
          letter-spacing: 0.02em;
          color: var(--red);
          font-weight: 600;
          margin-top: 12px;
        }
        .af-card-1 { top: 10px; left: 30px; transform: rotate(-7deg); z-index: 2; }
        .af-card-2 { top: 150px; left: 190px; transform: rotate(5deg); z-index: 3; background: var(--red); }
        .af-card-2 .en { color: var(--paper); }
        .af-card-2 .bullets { color: rgba(251,239,231,0.85); }
        .af-card-2 .cta { color: var(--paper); }
        .af-card-3 { top: 300px; left: 60px; transform: rotate(-3deg); z-index: 1; background: var(--gold); }
        .af-card-3 .en { color: var(--ink); }
        .af-card-3 .bullets { color: rgba(37,7,9,0.65); }
        .af-card-3 .cta { color: var(--ink); }

        .af-stats {
          background: var(--ink);
          display: flex;
          justify-content: center;
          gap: 80px;
          padding: 30px 24px;
          flex-wrap: wrap;
        }
        .af-stat { text-align: center; }
        .af-stat .num {
          font-family: 'Fraunces', serif;
          font-size: 30px;
          color: var(--gold);
          font-weight: 600;
        }
        .af-stat .label {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(251,239,231,0.6);
          margin-top: 4px;
        }

        .af-section {
          padding: 96px 48px;
          max-width: 1180px;
          margin: 0 auto;
        }
        .af-section-head {
          text-align: center;
          max-width: 560px;
          margin: 0 auto 56px;
        }
        .af-section-head .af-eyebrow { color: var(--red); margin-bottom: 12px; }
        .af-section-head h2 { font-size: clamp(26px, 4vw, 36px); color: var(--ink); }
        .af-section-head p { color: #6b4a45; margin-top: 14px; font-size: 15.5px; }

        .af-feature-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        .af-feature {
          background: white;
          border: 1px solid rgba(37,7,9,0.08);
          border-radius: 16px;
          padding: 30px;
        }
        .af-feature-icon {
          width: 44px; height: 44px;
          border-radius: 12px;
          background: linear-gradient(135deg, var(--red), var(--red-deep));
          display: flex; align-items: center; justify-content: center;
          color: var(--paper);
          margin-bottom: 18px;
        }
        .af-feature h3 { font-size: 18px; margin-bottom: 6px; color: var(--ink); }
        .af-feature .subtitle {
          font-size: 13.5px;
          color: var(--red);
          font-weight: 600;
          margin-bottom: 14px;
        }
        .af-feature ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .af-feature ul li {
          position: relative;
          font-size: 14px;
          color: #6b4a45;
          line-height: 1.6;
          padding-left: 20px;
          margin-bottom: 6px;
        }
        .af-feature ul li::before {
          content: '✓';
          position: absolute;
          left: 0;
          top: 0;
          color: var(--red);
          font-weight: 700;
        }

        .af-testimonial-section {
          background: var(--paper-dim);
          position: relative;
          overflow: hidden;
        }

        .af-bubbles {
          position: absolute;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
          z-index: 0;
        }

        .af-bubble {
          position: absolute;
          border-radius: 50%;
          opacity: 0.12;
          animation: floatBubble 8s ease-in-out infinite alternate;
        }

        .af-bubble-1 {
          width: 120px;
          height: 120px;
          background: var(--red);
          top: 5%;
          left: -2%;
          animation-delay: 0s;
        }
        .af-bubble-2 {
          width: 80px;
          height: 80px;
          background: var(--gold);
          bottom: 10%;
          right: 0%;
          animation-delay: 2s;
        }
        .af-bubble-3 {
          width: 60px;
          height: 60px;
          background: var(--red-deep);
          top: 40%;
          left: 15%;
          animation-delay: 4s;
        }
        .af-bubble-4 {
          width: 100px;
          height: 100px;
          background: var(--gold);
          bottom: 20%;
          left: 5%;
          animation-delay: 1s;
        }
        .af-bubble-5 {
          width: 50px;
          height: 50px;
          background: var(--red);
          top: 20%;
          right: 10%;
          animation-delay: 3s;
        }
        .af-bubble-6 {
          width: 70px;
          height: 70px;
          background: var(--red-deep);
          bottom: 40%;
          right: 20%;
          animation-delay: 5s;
        }

        @keyframes floatBubble {
          0% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(20px, -30px) scale(1.1); }
          66% { transform: translate(-15px, 20px) scale(0.9); }
          100% { transform: translate(10px, -10px) scale(1.05); }
        }

        .af-testimonial-wrapper {
          position: relative;
          z-index: 1;
        }

        .af-video-slider {
          max-width: 340px;
          margin: 0 auto;
          position: relative;
        }

        .af-video-card {
          position: relative;
          width: 100%;
          aspect-ratio: 9 / 16;
          border-radius: 18px;
          overflow: hidden;
          background: var(--ink);
          box-shadow: 0 24px 48px -18px rgba(0,0,0,0.4);
        }

        .af-video-card iframe {
          width: 100%;
          height: 100%;
          border: none;
          display: block;
        }

        .af-yt-target {
          width: 100%;
          height: 100%;
        }

        .af-video-badge {
          position: absolute;
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0,0,0,0.7);
          backdrop-filter: blur(8px);
          padding: 6px 16px;
          border-radius: 100px;
          color: white;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.3px;
          display: flex;
          align-items: center;
          gap: 6px;
          border: 1px solid rgba(255,255,255,0.1);
          pointer-events: none;
        }

        .af-video-badge .dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #4CAF50;
          display: inline-block;
          animation: pulseDot 2s infinite;
        }

        @keyframes pulseDot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        .af-slider-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 24px;
          margin-top: 24px;
        }

        .af-slider-btn {
          background: var(--paper);
          border: 1.5px solid rgba(37,7,9,0.12);
          border-radius: 50%;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all .2s ease;
          color: var(--ink);
          flex-shrink: 0;
        }
        .af-slider-btn:hover {
          background: var(--red);
          color: var(--paper);
          border-color: var(--red);
        }

        .af-slider-dots {
          display: flex;
          gap: 8px;
        }
        .af-slider-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: rgba(37,7,9,0.2);
          border: none;
          cursor: pointer;
          transition: all .25s ease;
          padding: 0;
        }
        .af-slider-dot.active {
          background: var(--red);
          width: 28px;
          border-radius: 6px;
        }

        .af-mute-btn {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.15);
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all .2s ease;
          z-index: 5;
        }
        .af-mute-btn:hover {
          background: rgba(0,0,0,0.8);
          transform: scale(1.05);
        }

        .af-autoplay-badge {
          position: absolute;
          top: 12px;
          left: 12px;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(8px);
          padding: 4px 12px;
          border-radius: 100px;
          color: white;
          font-size: 10px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 5px;
          border: 1px solid rgba(255,255,255,0.1);
          z-index: 5;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .af-autoplay-badge .play-icon {
          animation: pulsePlay 1.5s ease-in-out infinite;
        }

        @keyframes pulsePlay {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.9); }
        }

        .af-cta-band {
          background: linear-gradient(120deg, var(--red-deep), var(--red));
          padding: 70px 48px;
          text-align: center;
          border-radius: 28px;
          max-width: 1080px;
          margin: 0 auto 96px;
        }
        .af-cta-band h2 { color: var(--paper); font-size: clamp(24px, 4vw, 32px); margin-bottom: 14px; }
        .af-cta-band p { color: rgba(251,239,231,0.85); font-size: 16px; margin-bottom: 30px; }
        .af-cta-band .af-btn-primary { 
          background: var(--ink); 
          box-shadow: none;
          font-size: 18px;
          padding: 18px 44px;
        }
        .af-cta-band .af-btn-primary:hover { background: #3a0d10; }

        .af-footer {
          background: var(--bg-darker);
          color: rgba(251,239,231,0.55);
          padding: 40px 48px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 13px;
          flex-wrap: wrap;
          gap: 16px;
        }
        .af-footer .af-logo { color: var(--paper-dim); font-size: 17px; }
        .af-footer-links { 
          display: flex; 
          gap: 24px; 
          align-items: center;
        }
        .af-footer-links a { 
          color: rgba(251,239,231,0.55); 
          text-decoration: none;
          transition: color .2s;
          cursor: pointer;
          background: none;
          border: none;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
        }
        .af-footer-links a:hover { color: var(--paper); }
        .af-footer-copyright {
          color: rgba(251,239,231,0.4);
          font-size: 12px;
        }

        /* MODALS */
        .af-privacy-overlay, .af-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.8);
          backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 999999;
          padding: 20px;
          animation: af-fade .2s ease;
        }
        @keyframes af-fade { from { opacity: 0 } to { opacity: 1 } }
        
        .af-privacy-modal {
          background: #ffffff;
          border-radius: 24px;
          width: 100%;
          max-width: 780px;
          max-height: 90vh;
          overflow-y: auto;
          padding: 44px 48px;
          position: relative;
          box-shadow: 0 40px 80px -20px rgba(0,0,0,0.7);
          animation: af-pop .25s ease;
        }
        
        .af-modal {
          background: #ffffff;
          border-radius: 24px;
          width: 100%;
          max-width: 440px;
          max-height: 92vh;
          overflow-y: auto;
          padding: 36px 40px;
          position: relative;
          box-shadow: 0 40px 80px -20px rgba(0,0,0,0.7);
          animation: af-pop .25s ease;
        }
        
        @keyframes af-pop { from { opacity: 0; transform: translateY(10px) scale(0.98) } to { opacity: 1; transform: none } }
        
        .af-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(0,0,0,0.06);
          border: none;
          cursor: pointer;
          color: #666;
          padding: 6px;
          border-radius: 50%;
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all .2s ease;
        }
        .af-modal-close:hover { 
          background: rgba(0,0,0,0.12); 
          color: #000;
          transform: rotate(90deg);
        }
        
        .af-modal h3 {
          font-size: 26px;
          color: #1a1a1a;
          margin-bottom: 8px;
        }
        .af-modal .sub { 
          font-size: 15px; 
          color: #555; 
          margin-bottom: 28px;
          line-height: 1.6;
        }
        
        .af-field { margin-bottom: 20px; }
        .af-field label {
          display: block;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #555;
          margin-bottom: 6px;
          font-weight: 600;
        }
        .af-input-wrap {
          display: flex;
          align-items: center;
          gap: 10px;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          padding: 12px 16px;
          background: white;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .af-input-wrap:focus-within { 
          border-color: var(--red);
          box-shadow: 0 0 0 4px rgba(200,30,58,0.1);
        }
        .af-input-wrap input {
          border: none;
          outline: none;
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          flex: 1 1 auto;
          min-width: 0;
          background: transparent;
          color: #1a1a1a;
          caret-color: var(--red);
        }
        .af-input-wrap input::placeholder { color: #aaa; opacity: 1; }
        
        .af-optional {
          text-transform: none;
          font-family: 'Inter', sans-serif;
          font-weight: 400;
          letter-spacing: 0;
          color: #999;
          font-size: 11px;
        }
        .af-textarea {
          width: 100%;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          padding: 12px 16px;
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          color: #1a1a1a;
          background: white;
          outline: none;
          resize: vertical;
          min-height: 80px;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .af-textarea:focus { 
          border-color: var(--red);
          box-shadow: 0 0 0 4px rgba(200,30,58,0.1);
        }
        .af-textarea::placeholder { color: #aaa; }

        .af-form-error {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          background: #fff0f0;
          border: 1px solid #ffcdd2;
          color: #c62828;
          font-size: 14px;
          padding: 12px 16px;
          border-radius: 12px;
          margin-bottom: 18px;
          line-height: 1.5;
        }
        .af-form-error svg {
          flex-shrink: 0;
          margin-top: 1px;
        }
        .af-spin { animation: af-spin 0.8s linear infinite; }
        @keyframes af-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .af-btn:disabled { opacity: 0.7; cursor: not-allowed; }

        .af-modal .af-btn-primary { 
          width: 100%; 
          justify-content: center; 
          margin-top: 6px; 
          padding: 16px;
          font-size: 17px;
        }

        .af-privacy-modal h2 {
          font-family: 'Fraunces', serif;
          font-size: 32px;
          color: #1a1a1a;
          margin-bottom: 4px;
          text-align: center;
        }
        .af-privacy-modal h2 span { color: var(--red); }
        
        .af-privacy-modal .custom-underline {
          width: 60px;
          height: 4px;
          background: var(--red);
          margin: 8px auto 20px;
          border-radius: 2px;
        }
        
        .af-privacy-modal .company-name {
          text-align: center;
          font-weight: 700;
          color: #1a1a1a;
          font-size: 16px;
          margin-bottom: 2px;
        }
        
        .af-privacy-modal .date-text {
          text-align: center;
          font-size: 14px;
          color: #666;
          margin-bottom: 24px;
          font-weight: 500;
        }
        
        .af-privacy-modal .intro {
          color: #2d2d2d;
          font-size: 15px;
          line-height: 1.8;
          margin-bottom: 20px;
          padding: 16px 20px;
          background: #faf5f5;
          border-radius: 12px;
          border-left: 4px solid var(--red);
        }
        
        .af-privacy-modal .sub-heading {
          font-family: 'Fraunces', serif;
          font-size: 20px;
          color: var(--red);
          margin: 24px 0 12px;
          font-weight: 700;
        }
        
        .af-privacy-modal .terms-content section ul {
          list-style: none;
          padding: 0;
          margin: 0 0 4px 0;
        }
        
        .af-privacy-modal .terms-content section ul li {
          color: #2d2d2d;
          font-size: 15px;
          line-height: 1.8;
          padding: 10px 0 10px 28px;
          position: relative;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .af-privacy-modal .terms-content section ul li:last-child {
          border-bottom: none;
        }
        
        .af-privacy-modal .terms-content section ul li::before {
          content: '▸';
          position: absolute;
          left: 0;
          top: 10px;
          color: var(--red);
          font-weight: 700;
          font-size: 16px;
        }
        
        .af-privacy-modal .terms-content section ul li p {
          color: #2d2d2d;
          font-size: 15px;
          line-height: 1.8;
          margin: 0;
        }
        
        .af-privacy-modal .conclusion {
          color: #1a1a1a;
          font-weight: 600;
          margin-top: 20px;
          padding: 16px 20px;
          background: #faf5f5;
          border-radius: 12px;
          text-align: center;
          font-size: 15px;
          line-height: 1.7;
        }
        
        .af-privacy-modal .close-btn {
          background: var(--red);
          color: white;
          border: none;
          border-radius: 100px;
          padding: 16px 32px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 16px;
          cursor: pointer;
          width: 100%;
          margin-top: 24px;
          transition: all .2s ease;
        }
        .af-privacy-modal .close-btn:hover { 
          background: var(--red-deep);
          transform: scale(1.02);
        }

        .af-privacy-modal::-webkit-scrollbar,
        .af-modal::-webkit-scrollbar {
          width: 8px;
        }
        .af-privacy-modal::-webkit-scrollbar-track,
        .af-modal::-webkit-scrollbar-track {
          background: #f5f5f5;
          border-radius: 10px;
        }
        .af-privacy-modal::-webkit-scrollbar-thumb,
        .af-modal::-webkit-scrollbar-thumb {
          background: var(--red);
          border-radius: 10px;
        }
        .af-privacy-modal::-webkit-scrollbar-thumb:hover,
        .af-modal::-webkit-scrollbar-thumb:hover {
          background: var(--red-deep);
        }

        @media (max-width: 1024px) {
          .af-hero { padding: 72px 32px 90px; gap: 28px; }
          .af-section { padding: 80px 32px; }
          .af-feature-grid { grid-template-columns: repeat(2, 1fr); }
          .af-nav { padding: 18px 32px; }
        }

        @media (max-width: 900px) {
          .af-hero { grid-template-columns: 1fr; padding: 48px 24px 64px; }
          .af-hero-ctas .af-btn { 
            flex: 1 1 auto; 
            justify-content: center;
            font-size: 16px;
            padding: 16px 32px;
          }
          .af-cards { height: 320px; margin-top: 28px; max-width: 420px; }
          .af-card { width: 220px; padding: 15px 17px; }
          .af-card .en { font-size: 16px; }
          .af-card-1 { left: 0; top: 0; }
          .af-card-2 { left: 100px; top: 100px; }
          .af-card-3 { left: 10px; top: 210px; }

          .af-navright { display: none; }
          .af-menu-btn { display: block; }
          .af-nav { padding: 16px 20px; }
          .af-mobile-menu {
            display: flex;
            flex-direction: column;
            gap: 4px;
            background: var(--bg-dark);
            padding: 8px 20px 22px;
            border-top: 1px solid var(--line);
          }
          .af-mobile-menu a {
            color: var(--paper-dim);
            text-decoration: none;
            font-size: 15px;
            padding: 12px 0;
            border-bottom: 1px solid var(--line);
          }
          .af-mobile-menu .af-btn {
            font-size: 16px;
            padding: 16px 32px;
            width: 100%;
            justify-content: center;
          }

          .af-section { padding: 64px 20px; }
          .af-feature-grid { grid-template-columns: 1fr; }
          .af-stats { gap: 36px; padding: 26px 20px; }
          .af-cta-band { 
            border-radius: 0; 
            padding: 48px 24px;
          }
          .af-footer { 
            flex-direction: column; 
            text-align: center; 
            padding: 32px 24px;
          }
          .af-footer-links { justify-content: center; }
          .af-video-slider { max-width: 280px; }
          .af-bubble-1 { width: 60px; height: 60px; }
          .af-bubble-2 { width: 40px; height: 40px; }
          .af-bubble-3 { width: 30px; height: 30px; }
          .af-bubble-4 { width: 50px; height: 50px; }
          .af-bubble-5 { width: 25px; height: 25px; }
          .af-bubble-6 { width: 35px; height: 35px; }

          .af-privacy-modal { 
            padding: 30px 24px; 
            max-height: 92vh;
          }
          .af-privacy-modal h2 { font-size: 26px; }
          .af-privacy-modal .sub-heading { font-size: 18px; }
          
          .af-modal { 
            padding: 26px 24px; 
            max-height: 92vh;
          }
          .af-modal h3 { font-size: 22px; }
        }

        @media (max-width: 480px) {
          .af-announce { font-size: 11.5px; padding: 8px 12px; }
          .af-hero { padding: 40px 18px 52px; }
          .af-hero p.sub { font-size: 15.5px; margin: 18px 0 26px; }
          .af-hero-ctas { flex-direction: column; align-items: stretch; gap: 10px; }
          .af-hero-ctas .af-btn {
            font-size: 15px;
            padding: 14px 28px;
          }
          .af-cards { height: 280px; max-width: 100%; }
          .af-card { width: 78%; max-width: 210px; padding: 13px 15px; }
          .af-card .en { font-size: 15px; }
          .af-card-2 { left: unset; right: 0; top: 90px; }
          .af-card-3 { left: 4px; top: 190px; }
          .af-stats { gap: 24px 20px; }
          .af-stat .num { font-size: 24px; }
          .af-slider-btn { width: 36px; height: 36px; }
          .af-video-slider { max-width: 240px; }
          .af-cta-band h2 { font-size: 24px; }
          .af-cta-band .af-btn { 
            width: 100%; 
            justify-content: center;
            font-size: 15px;
            padding: 14px 28px;
          }
          .af-modal { 
            padding: 22px 18px; 
            border-radius: 16px;
          }
          .af-modal h3 { font-size: 20px; }
          .af-modal .af-btn-primary { 
            font-size: 15px; 
            padding: 14px;
          }
          .af-privacy-modal { 
            padding: 22px 18px;
            border-radius: 16px;
          }
          .af-privacy-modal h2 { font-size: 24px; }
          .af-privacy-modal .sub-heading { font-size: 17px; }
          .af-privacy-modal .terms-content section ul li p { font-size: 14px; }
        }
      `}</style>

      <div className="af-announce">
        <b>🎉 Get up to 25% OFF for a Limited Time</b>
      </div>

      <nav className="af-nav">
        <div className="af-logo">
          <img src="../../image.png" alt="Anglofone Logo" className="af-logo-img" />
        </div>
       
        <div className="af-navright">
          <button className="af-btn af-btn-primary af-btn-attn" onClick={() => setOpen(true)}>
            Join now <ArrowRight size={18} />
          </button>
        </div>
        <button className="af-menu-btn" aria-label="Toggle menu" onClick={() => setMenuOpen((v) => !v)}>
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {menuOpen && (
        <div className="af-mobile-menu">
          <a href="#courses" onClick={() => setMenuOpen(false)}>Courses</a>
          <a href="#teachers" onClick={() => setMenuOpen(false)}>Teachers</a>
          <a href="#pricing" onClick={() => setMenuOpen(false)}>Pricing</a>
          <a href="#stories" onClick={() => setMenuOpen(false)}>Stories</a>
          <button
            className="af-btn af-btn-primary af-btn-attn"
            onClick={() => { setMenuOpen(false); setOpen(true); }}
          >
            Join now <ArrowRight size={18} />
          </button>
        </div>
      )}

      <header className="af-hero">
        <div>
          <h1>Learn spoken English <em>through Tamil</em></h1>
          <p className="sub">
           🎉 Get up to 25% OFF for a Limited Time. Learn through practical lessons, real-life conversations, and expert guidance that help you speak English with confidence.
          </p>
          <div className="af-hero-ctas">
            <button className="af-btn af-btn-primary af-btn-attn" onClick={() => setOpen(true)}>
              Join now <ArrowRight size={20} />
            </button>
          </div>
        </div>

        <div className="af-cards">
          {CATEGORY_CARDS.map((c, i) => (
            <div className={`af-card af-card-${i + 1}`} key={c.title}>
              <div className="en">{c.title}</div>
              <div className="bullets">{c.bullets.join(" • ")}</div>
              <div className="cta">👉 {c.cta}</div>
            </div>
          ))}
        </div>
      </header>

      <div className="af-stats">
        <div className="af-stat">
          <div className="num">1Lakh+</div>
          <div className="label">Students Trained</div>
        </div>
        <div className="af-stat">
          <div className="num">4.8/5</div>
          <div className="label">Average rating</div>
        </div>
      </div>

      <section className="af-section" id="courses">
        <div className="af-section-head">
          <div className="af-eyebrow">WHY ANGLOFONE ?</div>
          <h2>From knowing English to using it</h2>
          <p>Personalised learning, practical conversations, and feedback that helps you speak with confidence.</p>
        </div>
        <div className="af-feature-grid">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div className="af-feature" key={f.title}>
                <div className="af-feature-icon">
                  <Icon size={20} />
                </div>
                <h3>{f.title}</h3>
                <div className="subtitle">{f.subtitle}</div>
                <ul>
                  {f.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section className="af-section af-testimonial-section" id="stories" ref={sectionRef}>
        <div className="af-bubbles">
          <div className="af-bubble af-bubble-1"></div>
          <div className="af-bubble af-bubble-2"></div>
          <div className="af-bubble af-bubble-3"></div>
          <div className="af-bubble af-bubble-4"></div>
          <div className="af-bubble af-bubble-5"></div>
          <div className="af-bubble af-bubble-6"></div>
        </div>

        <div className="af-testimonial-wrapper">
          <div className="af-section-head">
            <div className="af-eyebrow">STUDENT STORIES</div>
            <h2>Hear it from our students</h2>
            <p>Real students, real results — in their own words.</p>
          </div>
          <div className="af-video-slider">
            <div className="af-video-card">
              <div ref={videoContainerRef} className="af-yt-target" />
              <button 
                className="af-mute-btn"
                onClick={toggleMute}
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <div className="af-video-badge">
                <span className="dot"></span>
                Student Testimonial
              </div>
              <div className="af-autoplay-badge">
                <Play size={12} className="play-icon" />
                Auto-play
              </div>
            </div>

            <div className="af-slider-controls">
              <button
                className="af-slider-btn"
                onClick={goToPrevVideo}
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={20} />
              </button>

              <div className="af-slider-dots">
                {VIDEO_TESTIMONIALS.map((id, index) => (
                  <button
                    key={id}
                    className={`af-slider-dot ${index === currentVideo ? 'active' : ''}`}
                    onClick={() => goToSpecificVideo(index)}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <button
                className="af-slider-btn"
                onClick={goToNextVideo}
                aria-label="Next testimonial"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="af-section" style={{ paddingBottom: 0 }}>
        <div className="af-cta-band">
          <h2>Ready to Take Your English to the Next Level?</h2>
          <p>Tell us why you're learning, and we'll match you with the right teacher and track.</p>
          <button className="af-btn af-btn-primary af-btn-attn" onClick={() => setOpen(true)}>
            Join now <ArrowRight size={20} />
          </button>
        </div>
      </section>

      <footer className="af-footer">
        <div className="af-logo">
          <img src="../../image.png" alt="Anglofone Logo" className="af-logo-img" />
        </div>

        <div className="af-footer-links">
          <a onClick={() => setPrivacyOpen(true)}>Privacy Policy</a>
          <span className="af-footer-copyright">
            © Anglofone Edtech Pvt, 2025. All rights reserved.
          </span>
        </div>
      </footer>

      {/* JOIN MODAL */}
      {open && createPortal(
        <div className="af-overlay" onClick={closeModal}>
          <div className="af-modal" onClick={(e) => e.stopPropagation()}>
            <button className="af-modal-close" onClick={closeModal}><X size={20} /></button>

            <>
              <h3>Join Anglofone</h3>
              <p className="sub">Takes less than a minute. We'll message you on WhatsApp for further updates.</p>
              <form onSubmit={handleSubmit} autoComplete="off">
                <div className="af-field">
                  <label htmlFor="af-name">Your name <span style={{ color: '#c62828' }}>*</span></label>
                  <div className="af-input-wrap">
                    <input
                      id="af-name"
                      name="af-name"
                      type="text"
                      autoComplete="off"
                      placeholder="e.g. Priya Sharma"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="af-field">
                  <label htmlFor="af-phone">Phone number (WhatsApp) <span style={{ color: '#c62828' }}>*</span></label>
                  <div className="af-input-wrap">
                    <input
                      id="af-phone"
                      name="af-phone"
                      type="tel"
                      autoComplete="off"
                      placeholder="e.g. +91 98765 43210"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="af-field">
                  <label htmlFor="af-email">Email <span className="af-optional">(optional)</span></label>
                  <div className="af-input-wrap">
                    <input
                      id="af-email"
                      name="af-email"
                      type="email"
                      autoComplete="off"
                      placeholder="e.g. priya@email.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="af-field">
                  <label htmlFor="af-reason">Why do you want to learn English? <span className="af-optional">(optional)</span></label>
                  <textarea
                    id="af-reason"
                    className="af-textarea"
                    placeholder="e.g. I need it for a job interview next month..."
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    rows={3}
                  />
                </div>

                {status === "error" && (
                  <div className="af-form-error">
                    <AlertCircle size={16} />
                    {errorMsg}
                  </div>
                )}

                <button type="submit" className="af-btn af-btn-primary" disabled={status === "loading"}>
                  {status === "loading" ? (
                    <>
                      <Loader2 size={18} className="af-spin" /> Submitting...
                    </>
                  ) : (
                    <>Submit <ArrowRight size={18} /></>
                  )}
                </button>
              </form>
            </>
          </div>
        </div>,
        document.body
      )}

      {/* PRIVACY POLICY MODAL */}
      {privacyOpen && createPortal(
        <div className="af-privacy-overlay" onClick={() => setPrivacyOpen(false)}>
          <div className="af-privacy-modal" onClick={(e) => e.stopPropagation()}>
            <button className="af-modal-close" onClick={() => setPrivacyOpen(false)}>
              <X size={20} />
            </button>
            
            <h2>Privacy <span>Policy</span></h2>
            <div className="custom-underline"></div>
            <p className="company-name">AngloFone EdTech Pvt. Ltd</p>
            <p className="date-text">Last Updated on: 24.11.23</p>

            <p className="intro">
              Welcome to AngloFone EdTech's WhatsApp English Learning Module. This Privacy Policy outlines how we collect, use, disclose, and safeguard your personal information. By accessing our website or using our services, you agree to the terms of this Privacy Policy.
            </p>

            <div className="terms-content">
              <section>
                <h3 className="sub-heading">1. Information We Collect</h3>
                <ul>
                  <li>
                    <p>Personal Information: We may collect personal information, including but not limited to names, email addresses, contact numbers, and other relevant details provided by users during the registration process.</p>
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="sub-heading">2. How We Use Your Information</h3>
                <ul>
                  <li>
                    <p>Personalization: We use the collected information to personalize your learning experience, including providing relevant course recommendations and communication.</p>
                  </li>
                  <li>
                    <p>Communication: Your contact information may be used to send important updates, newsletters, course delivery and other relevant information.</p>
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="sub-heading">3. Information Sharing and Disclosure</h3>
                <ul>
                  <li>
                    <p>Third-Party Services: We may engage third-party services for analytics, communication, or other purposes. These parties have access to your information only to perform specific tasks and course delivery on our behalf.</p>
                  </li>
                  <li>
                    <p>Legal Requirements: We may disclose your information if required by law or in response to valid requests by public authorities.</p>
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="sub-heading">4. Your Privacy Choices</h3>
                <ul>
                  <li>
                    <p>Opt-Out: You may opt out of receiving promotional communications from us by following the unsubscribe link in our emails.</p>
                  </li>
                  <li>
                    <p>Access and Update: You can access and update your personal information by contacting us at admin@anglofone.co.in</p>
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="sub-heading">5. Data Security</h3>
                <ul>
                  <li>
                    <p>Security Measures: We implement security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction.</p>
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="sub-heading">6. Cookies and Similar Technologies</h3>
                <ul>
                  <li>
                    <p>Cookies: We use cookies to enhance your experience on our website. You can control or block cookies through your browser settings.</p>
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="sub-heading">7. External Links</h3>
                <ul>
                  <li>
                    <p>Third-Party Websites: Our website may contain links to external sites. We are not responsible for the content or privacy practices of these sites.</p>
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="sub-heading">8. Children's Privacy</h3>
                <ul>
                  <li>
                    <p>Age Restriction: Our services are not intended for individuals under the age of 13. We do not knowingly collect personal information from children.</p>
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="sub-heading">9. Changes to This Privacy Policy</h3>
                <ul>
                  <li>
                    <p>Updates: We may update this Privacy Policy to reflect changes in our practices. Please review this page periodically for any changes.</p>
                  </li>
                </ul>
              </section>

              <section>
                <h3 className="sub-heading">10. Contact Us</h3>
                <ul>
                  <li>
                    <p>Questions: If you have any questions or concerns regarding our Privacy Policy, please contact us at manager@anglofone.co.in</p>
                  </li>
                </ul>
              </section>
            </div>

            <p className="conclusion">
              By using AngloFone EdTech's services, you agree to the terms outlined in this Privacy Policy.
            </p>
            <p className="conclusion">Thank you for choosing AngloFone EdTech Pvt Ltd.</p>

            <button 
              className="close-btn"
              onClick={() => setPrivacyOpen(false)}
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}